import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import HashTag from "./HashTag";
import { getBlock } from "../data/notes";

const fade = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

/**
 * A note preview. The image / title / summary area links to the note page;
 * the tag chips are separate links (so we never nest anchors).
 * `showBlock` adds the parent block name (useful on tag pages).
 */
export default function NoteCard({ note, showBlock = false }) {
  const to = `/projects/${note.block}/${note.slug}`;
  const block = getBlock(note.block);
  const thumb = note.images?.[0];

  return (
    <motion.div
      variants={fade}
      className="group rounded-lg border border-border bg-surface/80 overflow-hidden transition-colors hover:border-muted-foreground/40 hover:bg-surface flex flex-col"
    >
      <Link to={to} className="block">
        {thumb && (
          <div className="overflow-hidden bg-background">
            <img
              src={thumb}
              alt={note.title}
              loading="lazy"
              className="w-full h-44 object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            />
          </div>
        )}
        <div className="p-5">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {showBlock && block && (
              <span className="px-2.5 py-0.5 rounded-full bg-surface-elevated text-muted text-[0.7rem] tracking-wide uppercase border border-border">
                {block.title}
              </span>
            )}
            {note.isNew && (
              <span className="px-2.5 py-0.5 rounded-full bg-accent/10 text-accent text-[0.7rem] tracking-wide uppercase border border-accent/20">
                New
              </span>
            )}
          </div>
          <h3 className="text-lg font-semibold text-foreground font-display mb-2 group-hover:text-accent transition-colors">
            {note.title}
          </h3>
          <p className="text-muted text-sm leading-relaxed">{note.summary}</p>
        </div>
      </Link>

      {note.tags?.length > 0 && (
        <div className="px-5 pb-5 mt-auto flex flex-wrap gap-2">
          {note.tags.slice(0, 5).map((t) => (
            <HashTag key={t} tag={t} variant="chip" />
          ))}
        </div>
      )}
    </motion.div>
  );
}
